import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import MarketingNav from "@/components/MarketingNav";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, ArrowLeft, Play, Copy, User, CheckCircle2, Pencil, Trash2, FileQuestion, GraduationCap } from "lucide-react";
import { quizImageUrl } from "@/lib/quizImage";

const LETTERS = ["A", "B", "C", "D", "E", "F"];
const TYPE_LABEL = {
  multiple_choice: "Múltipla escolha",
  true_false: "Verdadeiro ou falso",
  multiple_answers: "Múltiplas respostas",
};

export default function BibliotecaQuiz() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [quiz, setQuiz] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get(`/quizzes/public/${id}`)
      .then((r) => setQuiz(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  const isAdmin = user?.role === "admin";

  const apply = async () => {
    if (!user) {
      toast.info("Entre na sua conta para aplicar o quiz à sua turma.");
      navigate("/login");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/games/start", { quiz_id: id });
      navigate(`/apresentar/${res.data.session_id}`, { state: res.data });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao aplicar o quiz.");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!user) {
      toast.info("Entre na sua conta para copiar o quiz.");
      navigate("/login");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post(`/quizzes/${id}/copy`);
      toast.success(`Copiado como "${res.data.title}"`);
      navigate(`/quiz/${res.data.quiz_id}/editar`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao copiar o quiz.");
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    try {
      await api.delete(`/quizzes/${id}`);
      toast.success("Quiz excluído da biblioteca.");
      navigate("/biblioteca");
    } catch {
      toast.error("Erro ao excluir.");
    }
  };

  return (
    <div className="min-h-screen bg-brand-bg flex flex-col">
      <MarketingNav />
      <main className="flex-1 max-w-4xl mx-auto w-full px-5 sm:px-8 py-10">
        <button
          onClick={() => navigate("/biblioteca")}
          className="inline-flex items-center gap-1.5 text-sm text-brand-muted hover:text-brand-ink mb-6"
          data-testid="back-to-library"
        >
          <ArrowLeft className="w-4 h-4" /> Voltar à biblioteca
        </button>

        {notFound ? (
          <div className="text-center py-24">
            <FileQuestion className="w-12 h-12 text-slate-300 mx-auto" />
            <h2 className="mt-4 font-heading font-bold text-xl text-brand-ink">Quiz não encontrado</h2>
            <Link to="/biblioteca" className="mt-4 inline-block text-brand-blue font-semibold">
              Ver a biblioteca
            </Link>
          </div>
        ) : !quiz ? (
          <div className="flex justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-brand-purple" />
          </div>
        ) : (
          <>
            <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-100 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-brand-blue/10 text-brand-blue">
                  {quiz.subject || "Geral"}
                </span>
                {quiz.grade && (
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-brand-muted flex items-center gap-1">
                    <GraduationCap className="w-3.5 h-3.5" /> {quiz.grade}
                  </span>
                )}
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-brand-muted flex items-center gap-1">
                  <FileQuestion className="w-3.5 h-3.5" /> {quiz.question_count} questões
                </span>
              </div>
              <h1 className="mt-4 font-heading text-3xl sm:text-4xl font-black text-brand-ink" data-testid="quiz-title">
                {quiz.title}
              </h1>
              {quiz.description && <p className="mt-2 text-brand-muted">{quiz.description}</p>}
              <p className="mt-3 text-sm text-brand-muted flex items-center gap-1.5" data-testid="quiz-creator">
                <User className="w-4 h-4" /> Criado por <strong className="text-brand-ink">{quiz.creator_name}</strong>
              </p>

              <div className="mt-6 flex flex-wrap gap-3">
                <Button
                  onClick={apply}
                  disabled={busy}
                  className="rounded-full gap-2 bg-brand-green hover:bg-emerald-600"
                  data-testid="apply-quiz-btn"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Aplicar para minha
                  turma
                </Button>
                <Button
                  onClick={copy}
                  disabled={busy}
                  variant="outline"
                  className="rounded-full gap-2"
                  data-testid="copy-quiz-btn"
                >
                  <Copy className="w-4 h-4" /> Copiar para minha conta
                </Button>
                {isAdmin && (
                  <>
                    <Link to={`/quiz/${id}/editar`}>
                      <Button variant="outline" className="rounded-full gap-2" data-testid="admin-edit-btn">
                        <Pencil className="w-4 h-4" /> Editar
                      </Button>
                    </Link>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="outline"
                          className="rounded-full gap-2 text-red-500 hover:bg-red-50"
                          data-testid="admin-delete-btn"
                        >
                          <Trash2 className="w-4 h-4" /> Excluir
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Excluir quiz da biblioteca?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Esta ação remove o quiz permanentemente e não pode ser desfeita.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction onClick={del} className="bg-red-500 hover:bg-red-600">
                            Excluir
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </>
                )}
              </div>
              {!user && (
                <p className="mt-3 text-xs text-brand-muted">
                  Para aplicar ou copiar,{" "}
                  <Link to="/login" className="text-brand-blue font-semibold">
                    entre na sua conta
                  </Link>{" "}
                  ou{" "}
                  <Link to="/cadastro" className="text-brand-blue font-semibold">
                    crie uma grátis
                  </Link>
                  .
                </p>
              )}
            </div>

            {/* Pré-visualização das perguntas */}
            <h2 className="mt-10 font-heading font-bold text-xl text-brand-ink">Pré-visualização</h2>
            <div className="mt-4 space-y-4" data-testid="questions-preview">
              {quiz.questions.map((q, qi) => (
                <div key={q.id || qi} className="bg-white rounded-2xl p-5 sm:p-6 border border-slate-100 shadow-sm">
                  <div className="flex items-center justify-between text-xs text-brand-muted">
                    <span className="font-semibold uppercase tracking-wide">
                      Questão {qi + 1} · {TYPE_LABEL[q.type] || "Múltipla escolha"}
                    </span>
                    <span>{q.time_limit || 20}s</span>
                  </div>
                  <h3 className="mt-2 font-heading font-bold text-lg text-brand-ink">{q.question}</h3>
                  {q.image && (
                    <div className="mt-3">
                      <img
                        src={quizImageUrl(q.image)}
                        alt="Figura da questão"
                        className="max-h-64 rounded-xl object-contain border border-slate-200 bg-slate-50"
                      />
                    </div>
                  )}
                  <div className="mt-4 grid sm:grid-cols-2 gap-2.5">
                    {q.options.map((opt, oi) => {
                      const correct = (q.correct_answers || []).includes(oi);
                      return (
                        <div
                          key={oi}
                          className={`flex items-center gap-2.5 rounded-xl px-3.5 py-2.5 text-sm font-medium border ${
                            correct
                              ? "bg-emerald-50 border-emerald-300 text-emerald-800"
                              : "bg-slate-50 border-slate-100 text-brand-ink"
                          }`}
                        >
                          <span
                            className={`w-6 h-6 rounded-lg flex items-center justify-center font-bold text-xs shrink-0 ${
                              correct ? "bg-emerald-500 text-white" : "bg-white text-brand-muted border border-slate-200"
                            }`}
                          >
                            {LETTERS[oi]}
                          </span>
                          <span className="flex-1">{opt}</span>
                          {correct && <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />}
                        </div>
                      );
                    })}
                  </div>
                  {q.explanation && (
                    <p className="mt-3 text-sm text-brand-muted bg-slate-50 rounded-xl px-3.5 py-2.5">
                      <strong className="text-brand-ink">Explicação:</strong> {q.explanation}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
